const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const HRIS_SCOPE_PREFIXES = ['/api/hris/', '/api/employer/', '/api/org/', '/api/agent/', '/api/attendance', '/api/admin/verification', '/api/worker/directory', '/api/worker/training/streak'];

/**
 * True when the outgoing request body contains a `password` field —
 * i.e. the endpoint is doing a password re-confirmation (archive,
 * delete, sensitive-action gating). A 401 from these endpoints means
 * "wrong password", NOT "session expired".
 */
function isPasswordConfirmRequest(options) {
  if (!options || !options.body) return false;
  try {
    const raw = typeof options.body === 'string' ? options.body : '';
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.password === 'string';
  } catch {
    return false;
  }
}

/**
 * Friendly, status-code-specific fallback message used when the body
 * couldn't be read but we still got an HTTP status from the server.
 * Keeps users out of generic "clear your cache" purgatory.
 */
function statusFallbackMessage(status, endpoint, options) {
  const isAuthRegister = /\/auth\/register$/i.test(endpoint || '');
  const isAuthLogin = /\/auth\/login$/i.test(endpoint || '');
  const isAdminCreateUser = /\/admin\/users$/i.test(endpoint || '');
  const isEmployerCreateWorker = /\/employer\/workers\/create$/i.test(endpoint || '');
  const isPwdConfirm = isPasswordConfirmRequest(options);
  if (status === 400 && isAuthRegister) {
    return 'Could not create the account — this email may already be registered, or one of the fields is invalid. Try signing in instead, or use a different email.';
  }
  if (status === 400 && (isAdminCreateUser || isEmployerCreateWorker)) {
    return 'Could not create that user — one of the fields is invalid. Please check the email format, password length, and role.';
  }
  if (status === 409 && (isAdminCreateUser || isEmployerCreateWorker)) {
    return 'That email is already registered. Use a different email, or open the existing account to update their role.';
  }
  if (status === 401 && isAuthLogin) return 'Email or password is incorrect.';
  if (status === 401 && isPwdConfirm) return 'Incorrect password. Please try again.';
  if (status === 401) return 'Your session has expired — please sign in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'The requested resource was not found.';
  if (status === 409) return 'That resource already exists.';
  if (status === 413) return 'Upload too large.';
  if (status === 429) return 'Too many requests — please slow down and try again in a moment.';
  if (status >= 500) return `The server is having trouble right now (status ${status}). Please try again in a moment.`;
  return `Request failed with status ${status}.`;
}

/**
 * Read the response body using whichever strategy works.
 * Browser extensions, PostHog session-replay and other fetch interceptors
 * sometimes drain the body before our wrapper sees it. We try multiple
 * strategies in order so a partial failure of one doesn't break the call.
 * Returns { raw, failed }.
 */
async function readBodyResilient(response) {
  // Strategy 1: clone() + text() — safest, leaves the original intact for any
  // other observer that wants to read it.
  try {
    const txt = await response.clone().text();
    return { raw: txt, failed: false };
  } catch (e1) {
    if (process.env.NODE_ENV !== 'production') console.debug('api: clone().text() failed:', e1?.message);
  }

  // Strategy 2: text() on the original
  try {
    const txt = await response.text();
    return { raw: txt, failed: false };
  } catch (e2) {
    if (process.env.NODE_ENV !== 'production') console.debug('api: text() failed:', e2?.message);
  }

  // Strategy 3: arrayBuffer() — lowest-level, uses a different code path
  // inside browsers than text() and often still works when text() throws.
  try {
    const buf = await response.clone().arrayBuffer();
    return { raw: new TextDecoder().decode(buf), failed: false };
  } catch (e3) {
    if (process.env.NODE_ENV !== 'production') console.debug('api: arrayBuffer() failed:', e3?.message);
  }

  return { raw: '', failed: true };
}

/**
 * Centralised fetch wrapper.
 *
 * Hardened against third-party interceptors (PostHog session-replay,
 * Emergent debug monitor, browser extensions, service workers) that may
 * drain the response body stream before we get to it.
 *
 * Guarantees:
 *  - 2xx with unreadable body → returns {} (success, no payload)
 *  - 4xx/5xx → always throws an Error with a useful message, even if the
 *    body couldn't be read (uses status-code fallbacks)
 */
export async function downloadFile(endpoint, filename) {
  const res = await fetch(`${API_URL}${endpoint}`, { credentials: 'include' });
  if (!res.ok) throw new Error(statusFallbackMessage(res.status, endpoint, {}));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function apiCall(endpoint, options = {}) {
  // Phase B (iter90, Feb 22, 2026): auth is now cookie-only. We no longer
  // read a Bearer token from localStorage — the httpOnly `access_token`
  // cookie set by /api/auth/login is sent automatically via
  // `credentials: 'include'` below. Backend still accepts Bearer for tests
  // and CLI clients (they set the header explicitly via options.headers).
  const impersonateOrgId =
    typeof window !== 'undefined'
      ? sessionStorage.getItem('impersonate_org_id')
      : null;
  // Admin HRIS console "view as organisation" (read-only) — only for org-scoped HRIS paths.
  const hrisOrgScope = typeof window !== 'undefined' ? sessionStorage.getItem('hris_admin_org_scope') : null;
  const scopeApplies = hrisOrgScope && HRIS_SCOPE_PREFIXES.some(p => endpoint.startsWith(p));
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers = {
    // Auto-detected: skip explicit Content-Type when the caller passes
    // FormData so the browser can attach the correct multipart boundary.
    ...(!isFormData && { 'Content-Type': 'application/json' }),
    ...(impersonateOrgId && { 'X-Impersonate-Org-Id': impersonateOrgId }),
    ...(scopeApplies && { 'X-Org-Scope': hrisOrgScope }),
    ...options.headers,
  };

  let response;
  try {
    // credentials: 'include' opts every fetch into sending / receiving the
    // httpOnly `access_token` cookie set by /api/auth/login (Feb 20, 2026
    // iter76). Backward-compatible: existing Bearer/localStorage clients
    // continue to work — get_current_user still honors both. Phase B will
    // drop the Bearer header + localStorage entirely.
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });
  } catch (error) {
    if (error instanceof TypeError && /fetch/i.test(error.message)) {
      throw new Error('Network error: Unable to reach the server');
    }
    throw error;
  }

  const { raw, failed } = await readBodyResilient(response);

  // Body unreadable. 2xx → succeed silently; 4xx/5xx → throw helpful msg.
  if (failed) {
    if (response.ok) return {};
    throw new Error(statusFallbackMessage(response.status, endpoint, options));
  }

  let data = {};
  let bodyWasJson = true;
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch {
      // iter77: don't put raw HTML into data.detail — that let Cloudflare
      // 504 pages leak all the way into toast messages / logs. Fall back
      // cleanly and let statusFallbackMessage speak for the caller.
      bodyWasJson = false;
      data = {};
    }
  }

  if (!response.ok) {
    const fallback = statusFallbackMessage(response.status, endpoint, options);
    const rawDetail = bodyWasJson
      ? (data.detail || data.message || fallback)
      : fallback;
    // Backend often returns structured error objects like
    // { error_code, message, ... } — surface the human message so
    // toasts stay readable instead of dumping JSON at the user.
    let message = rawDetail;
    if (rawDetail && typeof rawDetail === 'object') {
      message = rawDetail.message || rawDetail.detail || JSON.stringify(rawDetail);
    }
    const err = new Error(
      typeof message === 'string' ? message : JSON.stringify(message)
    );
    if (rawDetail && typeof rawDetail === 'object') {
      err.detail = rawDetail;
    }
    err.status = response.status;
    throw err;
  }

  return data;
}

export { API_URL };
